<template>
    <v-autocomplete
        v-model="model"
        :items="items"
        :loading="loading"
        hide-no-data
        :item-title="label"
        item-value="personId"
        hide-selected
        :label="$t('select_user')"
        :placeholder="$t('start_typing')"
        prepend-icon="mdi-account"
        return-object
        clearable
        :rules="required ? [requiredRule('select_user')] : []"
        :disabled="disabled"
        :hint="isReadyForUpdate ? $t('save_and_update_selected_user') : ''"
        :persistent-hint="isReadyForUpdate"
        @update:model-value="change($event)"
        @update:search="onSearchChange"
    >
        <template v-slot:append>
            <v-btn
                v-if="canUpdate"
                :class="{'custom-btn': disabled && isReadyForUpdate}"
                color="primary"
                :disabled="!isReadyForUpdate"
                icon="mdi-check"
                variant="text"
                @click="update"
            />
        </template>
    </v-autocomplete>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, Watch, Setup} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '~/mixins/FormValidationRulesMixin.vue'
    import {isBlankItem, isEmpty, isEqual} from '@/utils/helpers'
    import {
        touristToUpdateUserRQ,
        userInfoToUpdateUserRQ,
        userGetToPutTransformer,
        corpTouristToUpdateUserRQ,
        privetClientGetToPutTransformer,
    } from '@/adapters/UpdateUserRQAdapter'
    import {
        touristToBasePassportRestAPI,
        userInfoToActivePassport,
        userInfoToBasePassportRestAPI,
    } from '@/adapters/BasePassportRestAPIAdapter'

    @Component
    export class TouristAutocomplete extends mixins(FormValidationRulesMixin) {
        @Prop() personId
        @Prop() companyId
        @Prop() tourist
        @Prop({default: () => [], type: Array}) selectedTourists
        @Prop({default: false, type: Boolean}) required
        @Prop({default: false, type: Boolean}) disabled
        @Prop({default: () => [], type: Array}) defaultEntries
        @Prop({default: false, type: Boolean}) canUpdate
        @Prop({default: false, type: Boolean}) disableSearch
        @Prop({default: false, type: Boolean}) isCorporate
        @Prop({default: {}}) firstNameLocalized
        @Prop({default: {}}) lastNameLocalized

        @Setup(() => useLocalized()) localized: Function

        @Watch('tourist', {deep: true})
        onPropsChanged() {
            if (this.isCorporate && !!this.model) {
                this.updateIsEqual()
            }
        }

        isEqualCorpTouristObj = false
        entries = []
        loading = false
        model = null
        search = null
        timer = null

        @Watch('tourist.firstName')
        onChangeFirstName(firstName) {
            if (this.canUpdate && this.model?.personId) return
            const person = this.model
            if (person && firstName !== person.firstName) {
                this.change({
                    firstName: firstName,
                    lastName: person.lastName,
                })

                this.model = null
            }
        }

        @Watch('tourist.lastName')
        onChangeLastName(lastName) {
            if (this.canUpdate && this.model?.personId) return
            const person = this.model
            if (person && lastName !== person.lastName) {
                this.change({
                    firstName: person.firstName,
                    lastName: lastName,
                })

                this.model = null
            }
        }

        // TODO Remove it and make by AbortController
        async onSearchChange(val) {
            this.search = val
            if (this.disableSearch) return
            if (this.timer) {
                clearTimeout(this.timer)
            }
            this.timer = window.setTimeout(() => {
                this.initSearch(val)
            }, 500)
        }

        @Emit()
        async change(val) {
            if (isEmpty(val)) {
                const {age, type} = this.tourist
                this.entries = this.defaultEntries
                return {
                    type,
                    ...(age && type === 'child' && {age}),
                }
            } else if (val.personId && !val.passports) {
                try {
                    const {
                        persons: [person],
                    } = await this.$api.privateClients.get({id: val.personId, limit: 1, companyId: this.companyId})
                    if (person.personId) this.model = person
                    return person
                } catch (e) {
                    return val
                }
            }
            return val
        }

        async mounted() {
            await this.$store.restored
            if (this.personId) {
                const defaultEntry = this.defaultEntries.find(({personId}) => personId === this.personId)
                if (defaultEntry) {
                    this.entries = this.defaultEntries
                    this.model = defaultEntry
                    await this.change(this.model)
                } else {
                    const entries = await this.getEntries({id: this.personId, limit: 1})
                    this.entries = entries
                    if (entries[0]) {
                        this.model = entries[0]
                        await this.change(this.model)
                    }
                }
            } else {
                this.entries = [...this.defaultEntries]
                const entry = this.entries.find(
                    ({firstName, lastName}) =>
                        firstName === this.tourist.firstName && lastName === this.tourist.lastName
                )
                if (entry) {
                    this.model = {...entry}
                }
            }
        }

        async getEntries(rq) {
            try {
                this.loading = true
                return this.isCorporate
                    ? (await this.$api.persons.get({...rq, locale: 'all'})).users
                    : (await this.$api[rq.pattern ? 'suggestPrivateClients' : 'privateClients'].get(rq)).persons
            } catch (e) {
                return []
            } finally {
                this.loading = false
            }
        }

        label({firstName, firstNameLocalized, lastName, lastNameLocalized, email, contactPhone, mobilePhone, prefix}) {
            let fullName = [prefix, firstName || firstNameLocalized, lastName || lastNameLocalized].filter(Boolean)
            if (this.isCorporate) {
                fullName = [prefix, this.localized(firstNameLocalized), this.localized(lastNameLocalized)].filter(
                    Boolean
                )
            }
            const contacts = [email, contactPhone || mobilePhone].filter(Boolean)
            return fullName.join(' ') + (contacts.length ? ` (${contacts.join(' ')})` : '')
        }

        async initSearch(pattern) {
            if (
                this.loading ||
                (this.entries.length > 0 && this.items.map(item => this.label(item)).indexOf(pattern) !== -1) ||
                !pattern ||
                (this.isCorporate && this.tourist.type === 'child')
            )
                return
            const entries = await this.getEntries({pattern, companyId: this.companyId})
            this.entries = [...entries, ...this.defaultEntries]
        }

        async update() {
            this.loading = true
            let success = true

            if ((!this.isCorporate && !this.isEqualTouristObj) || (this.isCorporate && !this.isEqualCorpTouristObj)) {
                try {
                    let tourist = {}
                    if (this.isCorporate && !this.isEqualCorpTouristObj) {
                        tourist = corpTouristToUpdateUserRQ(this.tourist, {
                            firstNameLocalized: this.firstNameLocalized,
                            lastNameLocalized: this.lastNameLocalized,
                        })
                        const touristInfo = Object.assign(this.model, tourist)
                        const args = [this.model.personId, userGetToPutTransformer(touristInfo)]

                        await this.$api.persons.put(...args)
                        this.model = clone(touristInfo)
                        this.updateIsEqual()
                    } else {
                        tourist = touristToUpdateUserRQ(this.tourist)
                        const touristInfo = Object.assign(this.model, tourist)
                        const args = [this.model.personId, privetClientGetToPutTransformer(touristInfo)]
                        await this.$api.privateClients.put(...args)
                        this.model = Object.assign(this.model, tourist)
                    }
                    const index = this.entries.findIndex(({personId}) => personId === this.model.personId)
                    this.entries[index] = {...this.entries[index], ...tourist}
                } catch (e) {
                    console.error(e)
                    success = false
                    this.$toast.error(this.$t('error_message.save_error'))
                }
            }
            if (
                !this.isBlankPassport &&
                !isEqual(touristToBasePassportRestAPI(this.tourist), userInfoToBasePassportRestAPI(this.model))
            ) {
                try {
                    let id = userInfoToActivePassport(this.model)?.id
                    if (!userInfoToActivePassport(this.model)) {
                        const args = [this.model.personId, touristToBasePassportRestAPI(this.tourist)]
                        const {passports} = this.isCorporate
                            ? await this.$api.personPassport.post(...args)
                            : await this.$api.crmPersonPassport.post(...args)
                        id = passports.id
                    } else {
                        const args = [this.model.personId, id, touristToBasePassportRestAPI(this.tourist)]
                        if (this.isCorporate) {
                            await this.$api.personPassport.put(...args)
                        } else {
                            await this.$api.crmPersonPassport.put(...args)
                        }
                    }
                    this.model.passports = [
                        {
                            ...userInfoToActivePassport(this.model),
                            ...touristToBasePassportRestAPI(this.tourist),
                            active: true,
                            id,
                        },
                    ]
                } catch (e) {
                    success = false
                    this.$toast.error(`${this.$t('passport')} - ${this.$t('error_message.save_error')}`)
                }
            }
            this.loading = false
            if (success) this.$toast.success(this.$t('success_message.saved_successfully'))
        }

        get items() {
            return this.entries.filter(
                entry =>
                    (this.model &&
                        ((this.model.personId && entry.personId === this.model.personId) ||
                            (!this.model.personId &&
                                this.model.firstName === entry.firstName &&
                                this.model.lastName === entry.lastName))) ||
                    !this.selectedTourists.find(
                        // TODO !selectedTourist.personId????
                        ({personId, firstName, lastName}) =>
                            (personId && personId === entry.personId) ||
                            (!personId && firstName === entry.firstName && lastName === entry.lastName)
                    )
            )
        }

        get isReadyForUpdate() {
            return (
                !this.loading &&
                this.canUpdate &&
                !!this.model &&
                ((!this.isCorporate && !this.isEqualTouristObj) ||
                    (this.isCorporate && !this.isEqualCorpTouristObj) ||
                    (!this.isBlankPassport &&
                        !isEqual(
                            touristToBasePassportRestAPI(this.tourist),
                            userInfoToBasePassportRestAPI(this.model)
                        )))
            )
        }

        get isEqualTouristObj() {
            return isEqual(touristToUpdateUserRQ(this.tourist), userInfoToUpdateUserRQ(this.model, false))
        }

        updateIsEqual() {
            this.isEqualCorpTouristObj = isEqual(
                corpTouristToUpdateUserRQ(this.tourist, {
                    firstNameLocalized: this.firstNameLocalized,
                    lastNameLocalized: this.lastNameLocalized,
                }),
                userInfoToUpdateUserRQ(this.model, true)
            )
        }

        get isBlankPassport() {
            const {type, ...passport} = touristToBasePassportRestAPI(this.tourist)
            return isBlankItem(passport)
        }
    }

    export default TouristAutocomplete
</script>

<style scoped lang="scss">
    :deep(.v-messages__message) {
        color: var(--v-primary-base) !important;
    }

    :deep(.v-input__append) {
        opacity: 1 !important;
    }
    .custom-btn {
        pointer-events: auto;
        opacity: 1;
    }
</style>
